import { useCallback, useRef, useState } from 'react'
import {
  DEFAULT_ASSIST_MODEL,
  parseAssistModelStatus,
  type AssistModel,
} from '@/pages/Meetings/components/Copilot/assistModel'

export type AssistModelErrorCode = 'bad_request' | 'not_supported'

interface UseAssistModelOptions {
  /**
   * The reader's remembered choice. Undefined for anyone who has never
   * pressed either button, which is everybody on the first release.
   */
  stored: AssistModel | undefined
  /** Remember a choice for the meetings after this one. */
  onRemember: (model: AssistModel) => void
  /**
   * Send `assist.model.update` on every socket this screen owns.
   *
   * @returns False when no socket was ready to take the frame — before the
   *   meeting starts, or on a backend that has no such event.
   */
  sendUpdate: (model: AssistModel) => boolean
}

/**
 * Which assistant answers Copilot, and the two frames that settle it.
 *
 * Shaped after {@link useRealtimeFeatureToggles}, which drives the other
 * value this screen puts on `config` and then changes mid-meeting. The
 * difference worth knowing is the scope: panes are remembered per meeting,
 * this is remembered per reader, so the store is what it is written back to.
 *
 * The reader's press does not move the buttons on its own. The server's
 * `assist.model.status` is the acknowledgment, and until it arrives the
 * panel still shows what questions are still going to — which is the truth,
 * and is also what `assist.model.update.error` leaves behind.
 */
export function useAssistModel({ stored, onRemember, sendUpdate }: UseAssistModelOptions) {
  const [selected, setSelected] = useState<AssistModel>(stored ?? DEFAULT_ASSIST_MODEL)
  /** Null until a status says; nothing is dimmed on a guess. */
  const [available, setAvailable] = useState<AssistModel[] | null>(null)
  /** The model a press is waiting on, which is what disables both buttons. */
  const [pending, setPending] = useState<AssistModel | null>(null)

  const selectedRef = useRef(selected)
  const pendingRef = useRef(pending)
  /**
   * Whether this server has ever acknowledged a model at all.
   *
   * Without it a backend that predates the feature would take the press, send
   * nothing back, and leave both buttons disabled for the rest of the meeting
   * — the one failure the reader cannot get out of.
   */
  const acknowledgesRef = useRef(false)
  const optionsRef = useRef({ onRemember, sendUpdate })
  optionsRef.current = { onRemember, sendUpdate }

  const commit = useCallback((model: AssistModel) => {
    selectedRef.current = model
    setSelected(model)
  }, [])

  /** The model to put on the next `config`, including after a reconnect. */
  const getAssistModel = useCallback((): AssistModel => selectedRef.current, [])

  /**
   * Ask for a model, because the reader pressed its button.
   *
   * The choice is remembered either way: it is theirs, and a meeting that
   * refuses it now should not also lose it for the next one.
   */
  const select = useCallback(
    (model: AssistModel) => {
      if (pendingRef.current || model === selectedRef.current) return
      optionsRef.current.onRemember(model)

      const sent = optionsRef.current.sendUpdate(model)
      // Nothing to wait for: no live socket, or one that will never answer.
      if (!sent || !acknowledgesRef.current) {
        commit(model)
        return
      }
      pendingRef.current = model
      setPending(model)
    },
    [commit]
  )

  /**
   * Apply `assist.model.status` — sent after `ready` and after every accepted
   * update, and the only thing that moves the buttons.
   *
   * @param event The frame. `selected: null` means the server is on its own
   *   default, which the panel shows as that default rather than as nothing.
   */
  const handleStatus = useCallback(
    (event: unknown) => {
      const status = parseAssistModelStatus(event)
      if (!status) return
      acknowledgesRef.current = true
      pendingRef.current = null
      setPending(null)
      setAvailable(status.available)
      commit(status.selected ?? DEFAULT_ASSIST_MODEL)
    },
    [commit]
  )

  /**
   * Apply `assist.model.update.error`. Non-fatal by contract: the socket is
   * still up, the old model is still answering, and the buttons go live again
   * so the reader can try the other one.
   */
  const handleError = useCallback((_code: AssistModelErrorCode) => {
    pendingRef.current = null
    setPending(null)
  }, [])

  return {
    selected,
    /**
     * Models this server did not name. Still pressable — the server answers
     * with the one it has rather than refusing, so this dims a button without
     * taking it away.
     */
    unavailable: (model: AssistModel) => available !== null && !available.includes(model),
    pending,
    getAssistModel,
    select,
    handleStatus,
    handleError,
  }
}
